import { LoadingOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authSignIn } from "../../../apiservice/authService";
import { IAuthType } from "../../../apiservice/authService.type";
import useFormatApiRequest from "../../../hooks/formatApiRequest";
import {
  USER_TOKEN_KEY,
  USER_AUTH_DATA_KEY,
  USER_REFRESH_TOKEN_KEY,
} from "../../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../../Redux/reduxCustomHook";
import { storePlainString, storeJSON } from "../../../utils/localStorage";
import { NotificationType } from "../../../utils/mscType.type";
import "../userAuth.css";
import { RootState } from "../../../Redux/store";

const UserSignin = () => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [loadApi, setLoadApi] = useState(false);
  const [user, setUser] = useState<any>({});
  const [api, contextHolder] = notification.useNotification();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const authData: IAuthType = useAppSelector(
    (state: RootState) => state?.AuthData
  );


  useEffect(() => {
    if (!authData?.data) {
      navigate("/auth");
    }
  }, [authData, navigate]); // Added authData and navigate as dependencies


  const handleInputChange = (event: any) => {
    const name = event.target.name;
    const value = event.target.value;
    setUser((values: any) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    setLoadApi(true);
    setFormLoading(true);
  };

  const result = useFormatApiRequest(
    () => authSignIn({ ...user, email: authData?.data?.credentials?.email }),
    loadApi,
    () => {
      setLoadApi(false);
    },
    () => {
      processApi();
    }
  );

  const processApi = async () => {
    if (result.httpState === "SUCCESS") {
      setFormLoading(false);
      const signinResult: IAuthType = result.data;
      storePlainString(USER_TOKEN_KEY, signinResult?.data?.accessToken || "");
      storePlainString(
        USER_REFRESH_TOKEN_KEY,
        signinResult?.data?.refreshToken || ""
      );
      storeJSON(USER_AUTH_DATA_KEY, signinResult);
      dispatch({ type: "AUTH_ADD_DATA", payload: signinResult });

      setTimeout(() => {
        navigate("/users/home");
      }, 1500);
      openNotificationWithIcon("info", "", "Login Success", "#D9FFB5");
    } else if (result.httpState === "ERROR") {
      setFormLoading(false);
      openNotificationWithIcon(
        "info",
        "",
        result.data?.response?.data?.errors?.[0].message ||
        result.data?.response?.data?.message ||
        result.errorMsg ||
        "Login Error",
        "#FFC2B7"
      );
    }
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
    description: string,
    background?: string
  ) => {
    api[type]({
      message,
      description,
      placement: "bottomRight",
      style: { background },
    });
  };

  return (
    <>
      {contextHolder}

      {/* Main Background */}
      {/* Assuming the blue theme is intentional for this quick sign-in page, even in dark mode.
          If global dark theme should be stone-900, then bg-blue-800 -> dark:bg-stone-900 */}
      <div className="flex flex-col min-h-screen bg-blue-800">
        {/* Logo Section */}
        <div className="w-full bg-stone-100 dark:bg-blue-700 flex flex-col items-center"> {/* Adjusted dark bg for this section */}
          <div className="p-4 pt-12 pb-12">
            <img
              src={`${process.env.PUBLIC_URL + "/images/logobluebig.svg"}`}
              alt="Logo"
              className="w-32 h-auto"
            />
            <br />

            <img
              src={`${process.env.PUBLIC_URL + "/images/logoblue.svg"}`}
              alt="Logo"
              className="w-32 h-auto"
            />
          </div>

          <div className="p-4 w-full rounded-t-3xl bg-blue-800">
            <p className="font-semibold fontKanitRegular text-xl text-center text-white">
              Continue as Login {authData?.data?.credentials?.fullName}
            </p>
            <p className="font-semibold fontKanitRegular text-small pt-4 text-center text-white">
              <Link to={"/auth"}> <u className="hover:text-gray-300"> Switch Account </u></Link>
            </p>
          </div>
        </div>

        {/* Blue Background Form */}
        {/* This part remains bg-blue-800 as per existing dark theme */}
        <div className="flex justify-center items-center p-8 bg-blue-800">
          <div className="w-full max-w-2xl p-4 rounded-xl">
            <form onSubmit={handleSubmit}>
              {/* Email Address */}
              <div className="relative w-full mb-6">
                <label className="absolute -top-3 left-3 bg-blue-800 px-1 text-white text-sm"> {/* Label bg matches form area bg */}
                  Email Address
                </label>
                <input
                  required
                  name="email"
                  value={authData?.data?.credentials?.email || user?.email}
                  readOnly
                  onChange={handleInputChange}
                  type="email"
                  className="pl-10 pr-4 py-2 border border-blue-500 dark:border-blue-500 rounded-lg w-full h-16 bg-transparent text-white placeholder-gray-300 dark:placeholder-blue-200"
                  placeholder="Enter your email"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MailOutlined className="text-white" />
                </div>
              </div>

              {/* Password */}
              <div className="relative w-full mb-2">
                <label className="absolute -top-3 left-3 bg-blue-800 px-1 text-white text-sm">
                  Password
                </label>
                <input
                  required
                  name="password"
                  value={user?.password}
                  onChange={handleInputChange}
                  type="password"
                  className="pl-10 pr-4 py-2 border border-blue-500 dark:border-blue-500 rounded-lg w-full h-16 bg-transparent text-white placeholder-gray-300 dark:placeholder-blue-200"
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockOutlined className="text-white" />
                </div>
              </div>

              {/* Forget Password */}
              <div className="flex items-end justify-end mb-6">
                <Link to={"/auth/user-forget-password"}>
                  <span className="text-white hover:text-gray-300 fontAlbertSansRegular">
                    Forget Password
                  </span>
                </Link>
              </div>

              {/* Login Button */}
              <div className="mb-2">
                <button className="w-full h-16 fontAlbertSansRegular bg-white hover:bg-gray-200 text-blue-800 rounded-xl text-lg">
                  {!formLoading ? (
                    "Sign In"
                  ) : (
                    <LoadingOutlined rev={undefined} />
                  )}
                </button>
              </div>
            </form>

            {/* Don’t have an account yet? Sign up */}
            <div className="mt-4 text-center text-white">
              <p>Don’t have an account yet?</p>
              <Link to={"/auth/signup"}>
                <span className="underline text-white hover:text-gray-300">Sign up</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSignin;
